import { Box, Stack, Typography } from "@mui/material";

export type Substat = {
  count: number;
  label: string;
  /** Theme color path for the dot and text, e.g. "warning.main". */
  color: string;
};

/**
 * The breakdown line under a stat card's number: a tinted dot and count per
 * substat.
 *
 * This exists so a figure that is a SUBSET of the card's headline number is
 * rendered as such rather than as a peer stat card beside it — three equal
 * cards for "active", "approaching deadline" and "overdue" invited reading the
 * counts as additive when the last two are already inside the first.
 *
 * Zero substats are dropped rather than shown as "0": a list of zeroes reads
 * as a problem list when there is no problem, so a clear card falls back to
 * `allClearLabel` instead. Omit that label where the headline number can itself
 * be a problem — an all-clear line under a non-zero error figure contradicts it.
 */
export function StatSubstats({
  substats,
  allClearLabel,
}: {
  substats: Substat[];
  allClearLabel?: string;
}) {
  const present = substats.filter((substat) => substat.count > 0);

  if (present.length === 0) {
    if (!allClearLabel) {
      return null;
    }
    return (
      <Typography variant="caption" sx={{ fontWeight: 600, color: "success.main" }}>
        {allClearLabel}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
      {present.map((substat) => (
        <Stack key={substat.label} direction="row" spacing={0.5} alignItems="center">
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: substat.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 600, color: substat.color }}>
            {substat.count} {substat.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
